// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable camelcase */

import { useMemo, useRef } from 'react';

import { useNotices } from '@/features/notices/useNotices';
import { ApiBatchUriItem, ApiJobDocument, useApi } from '@/shared/api';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

import {
  createOrUpdateEntity,
  getAssetIdentityValue,
  shouldMatchContact,
  shouldMatchPersonalId,
} from '../helpers';
import { useSmartImportStore } from '../stores/smartImport.store';
import { EntitiesList } from '../types';

const POLLING_INTERVAL_MS = 2 * 1000;

export const useSiActions = () => {
  const siStore = useSmartImportStore();
  const { keyPair } = useKeyPairStore();
  const api = useApi();
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const notices = useNotices();

  const sjFiles = useSjActions('fileMetaInfos');
  const sjEncryptedFileKey = useSjActions('encryptedFileKey');
  const sjAssets = useSjActions('assets');
  const sjPrivateDocs = useSjActions('privateDocuments');
  const sjPersonalIds = useSjActions('personalIdentifiersDocuments');
  const sjContacts = useSjActions('contacts');

  return useMemo(() => {
    const startPolling = (jobId: string, onReady: (job: ApiJobDocument) => void) => {
      const pollJob = async () => {
        const { data: jobData } = await api.aiGetJob(jobId);
        if (!jobData) return;
        onReady(jobData);
      };

      if (intervalId.current) return;
      intervalId.current = setInterval(pollJob, POLLING_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (!intervalId.current) return;
      clearInterval(intervalId.current);
      intervalId.current = null;
    };

    const extractFiles = async (filePaths: string[]) => {
      const items = filePaths.reduce<ApiBatchUriItem[]>((acc, path) => {
        try {
          const metaInfo = sjFiles?.findOne((file) => file.filePath === path);
          const encrypted = sjEncryptedFileKey?.findOne(({ id }) => id.includes(path));
          if (!metaInfo) return acc;

          const data: ApiBatchUriItem = {
            file_id: metaInfo.id,
            content_uri: metaInfo.filePath,
            symmetric_key: encrypted?.symmetricKey,
          };

          acc.push(data);
          return acc;
        } catch {
          notices.error('Something went wrong!');
          return [];
        }
      }, []);

      siStore.setIsLoading(true);

      const { data: batchData } = await api.aiBatchFilesByUri({ items });
      if (!batchData) return;

      siStore.setJobId(batchData.job_id);
      siStore.setIsLoading(false);
    };

    const saveEntities = (list: EntitiesList) => {
      const publicKey = keyPair?.publicKey;
      if (!publicKey) return;

      for (const asset of list.assets) {
        const identityValue = getAssetIdentityValue(asset);

        const existingItems = sjAssets?.findMany((targetAsset) => {
          if (targetAsset.category !== asset.category) return false;

          const targetIdentityValue = getAssetIdentityValue(targetAsset);
          return Boolean(targetIdentityValue) && identityValue === targetIdentityValue;
        });

        createOrUpdateEntity({
          collection: sjAssets,
          existingItems,
          newData: asset,
          publicKey,
        });
      }

      for (const personalDocument of list.privateDocuments) {
        sjPrivateDocs?.create(personalDocument, [publicKey]);
      }

      for (const personalId of list.personalIdentifiers) {
        const existingItems = sjPersonalIds.findMany((item) =>
          shouldMatchPersonalId(item, personalId),
        );

        createOrUpdateEntity({
          collection: sjPersonalIds,
          existingItems,
          newData: personalId,
          publicKey,
        });
      }

      for (const contact of list.contacts) {
        const existingItems = sjContacts?.findMany((c) => shouldMatchContact(c, contact));

        createOrUpdateEntity({
          collection: sjContacts,
          existingItems,
          newData: contact,
          publicKey,
        });
      }
    };

    const resetStores = () => {
      siStore.clear();
    };

    return { startPolling, stopPolling, extractFiles, saveEntities, resetStores };
  }, [api, siStore]);
};
